/**
 * RichTextContent component, for displaying rich text content in read-only mode.
 * @param {Object} props - The component props.
 * @param {string} props.content - The HTML content to display.
 * @returns {React.ReactElement} The rendered read-only rich text viewer.
 */

import React, { useContext, useEffect, useState } from 'react';
import './RichTextContent.scss';
import { useEditor, EditorContent } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import {
    Color,
    FontFamily,
    FontSize,
    TextStyle,
} from '@tiptap/extension-text-style';
import { ListItem } from '@tiptap/extension-list';
import { Placeholder } from '@tiptap/extensions';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Link from '@tiptap/extension-link';
import {
    Table,
    TableCell,
    TableHeader,
    TableRow,
} from '@tiptap/extension-table';
import Emoji from '../../utils/tiptap/EmojiExtension';
import ResizableImage from '../../utils/tiptap/ResizableImageExtension';
import { VideoNode } from '../new-portal-page/VideoNode';
import { QRCodeNode } from '../new-portal-page/QRCodeNode';
import Highlight from '@tiptap/extension-highlight';
import DivParagraph from '../new-portal-page/DivParagraph';
import { PortalLinkNode } from '../new-portal-page/PortalLinkNode';
import { ModalContext, ModalTypes } from '../../store/ModalContext';
import { useMenuLinkNavigation } from '../../hooks/useMenuLinkNavigation';
import { resolveSSOLinks } from '../../utils/resolveSSOLinks';
import { handleSSOLoginFailedLinkClick } from '../../utils/ssoLinkClickHandler';
import { getText } from '../../i18n';

function isAssetAnchor(anchor) {
    if (!anchor) {
        return false;
    }

    if (anchor.getAttribute('data-link-type') === 'asset') {
        return true;
    }

    const href = anchor.getAttribute('href') || '';
    return /\/files\/asset\//i.test(href);
}

const RichTextContent = ({ content }) => {
    const { showModal } = useContext(ModalContext);
    const [resolvedContent, setResolvedContent] = useState(content || '');

    useEffect(() => {
        let isCurrent = true;

        resolveSSOLinks(content || '').then(nextContent => {
            if (isCurrent) {
                setResolvedContent(nextContent || '');
            }
        });

        return () => {
            isCurrent = false;
        };
    }, [content]);

    const CustomTableCell = TableCell.extend({
        addAttributes() {
            return {
                ...this.parent?.(),
                backgroundColor: {
                    default: null,
                    parseHTML: element =>
                        element.getAttribute('data-background-color'),
                    renderHTML: attributes => {
                        return {
                            'data-background-color': attributes.backgroundColor,
                            style: `background-color: ${attributes.backgroundColor}`,
                        };
                    },
                },
            };
        },
    });

    const editor = useEditor({
        extensions: [
            Color.configure({ types: [TextStyle.name, ListItem.name] }),
            TextStyle.configure({ types: [ListItem.name] }),
            FontFamily.configure({
                types: ['textStyle'],
            }),
            FontSize,
            PortalLinkNode.configure({
                interactive: true,
            }),
            StarterKit.configure({
                // StarterKit v3 bundles these; keep the explicitly configured ones.
                link: false,
                underline: false,
                trailingNode: false,
                bulletList: {
                    keepMarks: true,
                    keepAttributes: false,
                },
                orderedList: {
                    keepMarks: true,
                    keepAttributes: false,
                },
            }),
            Placeholder.configure({
                placeholder: getText('msg_8b4539a50a3b'),
            }),
            Underline,
            Highlight.configure({ multicolor: true }),
            TextAlign.configure({
                types: ['heading', 'paragraph'],
            }),
            Link.configure({
                protocols: ['ftp', 'mailto'],
                HTMLAttributes: {
                    class: '',
                },
            }),
            ResizableImage,
            Table.configure({
                resizable: true,
            }),
            TableRow,
            TableHeader,
            TableCell,
            CustomTableCell,
            Emoji,
            VideoNode,
            QRCodeNode,
            DivParagraph,
        ],
        content: normalizeHtmlForEditorLoad(resolvedContent),
        editable: false, // READ-ONLY
    });

    useMenuLinkNavigation(editor);

    function normalizeHtmlForEditorLoad(html) {
        if (typeof html !== 'string' || !html) return html || '';

        return html.replace(/<div>\s*<br\s*\/?>\s*<\/div>/gi, '<div></div>');
    }

    function isEditorReadyForSerialization(currentEditor) {
        if (!currentEditor || currentEditor.isDestroyed) {
            return false;
        }

        try {
            return Boolean(
                currentEditor.state &&
                currentEditor.view &&
                currentEditor.schema,
            );
        } catch (error) {
            return false;
        }
    }

    React.useEffect(() => {
        if (!isEditorReadyForSerialization(editor)) {
            return;
        }

        try {
            const currentHtml = editor.getHTML();
            if (resolvedContent !== currentHtml) {
                const normalizedContent =
                    normalizeHtmlForEditorLoad(resolvedContent);
                editor.commands.setContent(normalizedContent, {
                    emitUpdate: false,
                });
            }
        } catch (error) {
            return;
        }
    }, [resolvedContent, editor]);

    React.useEffect(() => {
        return () => {
            if (editor && !editor.isDestroyed) {
                editor.destroy();
            }
        };
    }, [editor]);

    function handleContentClickCapture(event) {
        if (handleSSOLoginFailedLinkClick(event)) {
            return;
        }

        const assetAnchor = event.target.closest('a');

        if (!isAssetAnchor(assetAnchor)) {
            return;
        }

        const href = assetAnchor.getAttribute('href');
        if (!href) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();

        showModal({
            type: ModalTypes.ASSET_VIEW,
            title: assetAnchor.textContent?.trim() || 'Asset Preview',
            text: href,
        });
    }

    if (!editor) return null;

    return (
        <>
            <div onClickCapture={handleContentClickCapture}>
                <EditorContent
                    editor={editor}
                    className="custom-readonly-editor"
                />
            </div>
        </>
    );
};

export default RichTextContent;
